import * as React from 'react';
import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useExtensionGroups } from '../context/ExtensionGroupsContext';
import { IExtensionGroup, ISegmentRule } from '../../models/extension-group.models';
import Button from '@app/react-common/components/button/Button';
import ThreeDotMenu from '@app/react-common/components/three-dot-menu/ThreeDotMenu';
import { ConfirmationModal } from '@app/react-common/components/confirmation-modal/ConfirmationModal';
import { createPortal } from 'react-dom';
import { ExtensionsGroupComponentList } from './ExtensionsGroupComponentList';
import { ExtensionStatusDots } from './components/ExtensionStatusDots';
import Input from '@app/react-common/components/input/Input';
import { RuleEngineV2 } from '@app/react-common/components/rule-engine-v2/RuleEngineV2';
import { CheckoutSegmentsRuleEngineConfig } from '../../../checkout/react/config/CheckoutSegmentRuleEngineConfig';
import { Property, RuleEngineConfig } from '@app/react-common/components/rule-engine-v2/models/RuleEngineConfig';
import { RuleEngineState } from '@app/react-common/components/rule-engine-v2/models/RuleEngineState';
import { useAppContext } from '@app/react-common/AppContext';
import { usePdqCoreApi } from '@app/react-common/api/usePdqCoreApi';
import { getKeyByStringValue } from '@utils/string-utils';
import { countryCodeToDisplayNameMap } from '@pdqdev/pdq-shared/dist/utils/Country-utils';
import {
  ExtensionsGroupType,
  buildExtensionsGroupUsageToken,
} from '@pdqdev/pdq-shared/dist/interfaces/ICheckoutExtensions';
import Tag from '@app/react-common/components/tag/Tag';
import {
  ExtensionGroupModal,
  ExtensionGroupFormData,
  ExtensionGroupModalMode,
} from './extension-group-modal/ExtensionGroupModal';
import { EXTENSIONS_RULES } from '@app/react-common/utils/RulesByFeatureType';

export enum ExtensionGroupStatus {
  Enabled = 'Enabled',
  EnabledSegmented = 'EnabledSegmented',
  AbTest = 'AbTest',
  AbTestSegmented = 'AbTestSegmented',
  Disabled = 'Disabled',
  DisabledSegmented = 'DisabledSegmented',
}

export const getExtensionGroupStatus = (group: IExtensionGroup): ExtensionGroupStatus => {
  const isSegmented = !!group.segmentRules?.length;

  if (group.abTested) {
    return isSegmented ? ExtensionGroupStatus.AbTestSegmented : ExtensionGroupStatus.AbTest;
  }
  if (group.enabled) {
    return isSegmented ? ExtensionGroupStatus.EnabledSegmented : ExtensionGroupStatus.Enabled;
  }
  return isSegmented ? ExtensionGroupStatus.DisabledSegmented : ExtensionGroupStatus.Disabled;
};

const ExtensionsGroupTypeBadge: React.FC<{ groupType: ExtensionsGroupType }> = ({ groupType }) => (
  <Tag
    size="small"
    variant={groupType === ExtensionsGroupType.UPSELL ? 'danger' : 'tertiary'}
    value={groupType === ExtensionsGroupType.UPSELL ? 'Upsell' : 'Extensions'}
  />
);

const LIVE_GROUP_WARNING_MESSAGE =
  'You cannot edit segment rules while extension group is live. Please disable the extension group first.';

interface ExtensionGroupsViewProps {}

export const ExtensionGroupsMain: React.FC<ExtensionGroupsViewProps> = () => {
  const { extensionGroups, selectedGroup, setSelectedGroup, isLoading, segmentRuleActions, groupActions } =
    useExtensionGroups();
  const { showToast } = useAppContext();

  const isGroupLiveAndShowWarning = (group: IExtensionGroup): boolean => {
    if (group.enabled) {
      showToast({ message: LIVE_GROUP_WARNING_MESSAGE, mode: 'warning' });
      return true;
    }
    return false;
  };
  const sidebarRef = useRef<HTMLDivElement>(null);
  const [hasSidebarScrolling, setHasSidebarScrolling] = useState(false);

  const failureHandler = useCallback(async () => {
    showToast({ message: 'An error has occurred. Please contact support if the problem persists.', mode: 'danger' });
  }, [showToast]);
  const pdqCoreApi = usePdqCoreApi(failureHandler);

  const extensionGroupsRuleEngineConfig: RuleEngineConfig = useMemo(() => {
    return {
      properties: CheckoutSegmentsRuleEngineConfig.properties.filter((prop) =>
        EXTENSIONS_RULES.includes(prop.property),
      ),
    };
  }, []);

  const selectedGroupType = selectedGroup?.type ?? ExtensionsGroupType.EXTENSIONS;
  const selectedGroupUsageToken = selectedGroup?.id
    ? buildExtensionsGroupUsageToken(selectedGroupType, selectedGroup.id)
    : '';

  const [modals, setModals] = useState({
    createOpen: false,
    renameOpen: false,
    deleteOpen: false,
    duplicateOpen: false,
  });

  // Segment modals state
  const [showRenameSegmentModal, setShowRenameSegmentModal] = useState<number | null>(null);
  const [segmentToRemove, setSegmentToRemove] = useState<ISegmentRule | null>(null);
  const [renameSegmentValue, setRenameSegmentValue] = useState('');

  useEffect(() => {
    const checkSidebarScrolling = () => {
      if (sidebarRef.current) {
        const hasScroll = sidebarRef.current.scrollHeight > sidebarRef.current.clientHeight;
        setHasSidebarScrolling(hasScroll);
      }
    };

    checkSidebarScrolling();

    const resizeObserver = new ResizeObserver(checkSidebarScrolling);
    if (sidebarRef.current) {
      resizeObserver.observe(sidebarRef.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, [extensionGroups]);

  if (isLoading) {
    return <div className="extension-groups-view">Loading...</div>;
  }

  const AddExtensionGroupButton = (
    <div className="extension-group-add-button-container">
      <Button
        className="extension-group-add-button"
        onClick={() => {
          setModals((prev) => ({ ...prev, createOpen: true }));
        }}
        disabled={false}
        text={'Add extension group'}
        size={'medium'}
        variant={'primary'}
      />
    </div>
  );

  const getExtensionGroupMenuItems = (group: IExtensionGroup) => {
    const menuItems = [];

    if (!group.enabled) {
      menuItems.push({
        label: 'Set live',
        onClick: () => {
          groupActions.toggleGroupEnabled(group.id, true);
        },
      });
    } else {
      menuItems.push({
        label: 'Disable',
        onClick: () => {
          groupActions.toggleGroupEnabled(group.id, false);
        },
      });
    }

    menuItems.push({
      label: 'Add segment',
      onClick: () => {
        if (isGroupLiveAndShowWarning(group)) return;
        segmentRuleActions.createSegmentRule(group.id, {
          name: 'Segment name',
          rules: null,
        });
      },
    });

    menuItems.push({
      label: 'Rename',
      onClick: () => {
        setModals((prev) => ({ ...prev, renameOpen: true }));
      },
    });

    if (!group.enabled) {
      menuItems.push({
        label: 'Delete',
        onClick: () => {
          setModals((prev) => ({ ...prev, deleteOpen: true }));
        },
        variant: 'danger',
      });
    }

    return menuItems;
  };

  return (
    <div className="extension-groups-view">
      <div className="extension-groups-view__left-column">
        <div
          ref={sidebarRef}
          className={`extension-groups-view__sidebar ${!hasSidebarScrolling ? 'extension-groups-view__sidebar--no-scroll' : ''}`}>
          {extensionGroups?.map((group) => (
            <div
              key={group.id}
              className={`extension-groups-view__sidebar__item ${
                group.id === selectedGroup?.id ? 'extension-groups-view__sidebar__item__active' : ''
              }`}
              onClick={() => setSelectedGroup(group)}>
              <span className="extension-groups-view__sidebar__item__text">{group.name}</span>
              <ExtensionsGroupTypeBadge groupType={group.type ?? ExtensionsGroupType.EXTENSIONS} />
              <div className="extension-groups-view__sidebar__item__status">
                <ExtensionStatusDots status={getExtensionGroupStatus(group)} />
              </div>
            </div>
          ))}
          <div>
            {document.getElementById('header-portal-content') &&
              createPortal(AddExtensionGroupButton, document.getElementById('header-portal-content'))}
          </div>
        </div>
      </div>

      <div className="extension-groups-view__content">
        {selectedGroup ? (
          <>
            <div className="extension-groups-view__content__header">
              <div>
                <div className="extension-groups-view__content__header__title-row">
                  <h1 className="extension-groups-view__content__header__title">{selectedGroup.name}</h1>
                  <ExtensionsGroupTypeBadge groupType={selectedGroupType} />
                </div>
                {selectedGroup.description && (
                  <p className="extension-groups-view__content__header__desc">{selectedGroup.description}</p>
                )}
                <div className="extension-groups-view__content__header__token">
                  <span className="extension-groups-view__content__header__token__value">
                    Shopify token: {selectedGroupUsageToken}
                  </span>
                  <Button
                    className="extension-groups-view__content__header__token__copy"
                    onClick={() => navigator.clipboard.writeText(selectedGroupUsageToken)}
                    iconUrl="assets/icons/copy-to-clipboard.svg"
                    ariaLabel="Copy Shopify token"
                    size="medium"
                    variant="ghost-light"
                  />
                </div>
              </div>
              <div className="extension-groups-view__content__header__actions">
                <div className="extension-groups-view__content__header__actions__threedot">
                  <ThreeDotMenu items={getExtensionGroupMenuItems(selectedGroup)} />
                </div>
              </div>
            </div>

            {selectedGroup.segmentRules?.map((segment, index) => (
              <React.Fragment key={`${selectedGroup.id}-${segment.id ?? segment.internalId}-${segment.rerender}`}>
                <div className="segment">
                  <div className={`segment-header ${segment.expanded ? 'header-expanded' : ''}`}>
                    <div
                      className={`segment-header__expander ${segment.expanded ? 'rotate' : ''}`}
                      onClick={() =>
                        segmentRuleActions.expandSegmentRule(selectedGroup.id, segment.id ?? segment.internalId)
                      }
                    />
                    <span
                      className="segment-header__title"
                      onClick={() =>
                        segmentRuleActions.expandSegmentRule(selectedGroup.id, segment.id ?? segment.internalId)
                      }>
                      {segment.name}
                    </span>
                    {segmentRuleActions.checkIfSegmentRuleHasBeenEdited(
                      selectedGroup.id,
                      segment.id ?? segment.internalId,
                    ) && (
                      <Button
                        className="segment-header__cancel-button"
                        onClick={() =>
                          segmentRuleActions.cancelSegmentRuleEdits(selectedGroup.id, segment.id ?? segment.internalId)
                        }
                        text={'Cancel'}
                        size={'medium'}
                        variant={'secondary'}
                      />
                    )}
                    {segmentRuleActions.checkIfSegmentRuleHasBeenEdited(
                      selectedGroup.id,
                      segment.id ?? segment.internalId,
                    ) && (
                      <Button
                        className="segment-header__save-button"
                        onClick={() => {
                          if (selectedGroup.enabled) {
                            showToast({
                              message: LIVE_GROUP_WARNING_MESSAGE,
                              mode: 'warning',
                            });
                            return;
                          }
                          segmentRuleActions.saveSegmentRuleToApi(selectedGroup.id, segment.id ?? segment.internalId);
                        }}
                        text={'Save'}
                        size={'medium'}
                        variant={'primary'}
                      />
                    )}
                    <ThreeDotMenu
                      items={[
                        {
                          label: 'Rename',
                          onClick: () => {
                            if (isGroupLiveAndShowWarning(selectedGroup)) return;
                            setRenameSegmentValue(segment.name);
                            setShowRenameSegmentModal(segment.id ?? segment.internalId);
                          },
                        },
                        {
                          label: 'Delete',
                          onClick: () => {
                            if (isGroupLiveAndShowWarning(selectedGroup)) return;
                            setSegmentToRemove(segment);
                          },
                          variant: 'danger',
                        },
                      ]}
                    />
                  </div>
                  <div className={`segment-content ${segment.expanded ? 'seg-expanded' : 'seg-not-expanded'}`}>
                    <RuleEngineV2
                      key={`rule-engine-${selectedGroup.id}-${segment.id ?? segment.internalId}-${segment.rerender}`}
                      config={extensionGroupsRuleEngineConfig}
                      initialState={
                        segment.rules ?? {
                          matchAllRuleGroups: true,
                          ruleGroups: [],
                        }
                      }
                      bindings={{
                        onChange: (state: RuleEngineState) => {
                          segmentRuleActions.updateSegmentRuleState(
                            selectedGroup.id,
                            segment.id ?? segment.internalId,
                            state,
                          );
                        },
                        showValidationErrors: false,
                        fetchAutocompleteValues: async (property: Property, input: string = ''): Promise<string[]> => {
                          if (property === Property.country) {
                            return Object.values(countryCodeToDisplayNameMap);
                          }
                          const returnObject = await pdqCoreApi.get(
                            `/conditions/autocomplete/fetch?input=${encodeURIComponent(input)}&autocompleteBy=${getKeyByStringValue(Property, property)}`,
                          );
                          return returnObject.data;
                        },
                        showToast,
                      }}
                    />
                  </div>
                </div>
                {index < (selectedGroup.segmentRules?.length ?? 0) - 1 && <div className="segment-splitter">OR</div>}
              </React.Fragment>
            ))}

            <ExtensionsGroupComponentList />
          </>
        ) : (
          <div className="extension-groups-view__content__empty">
            <p>Select an extension group from the sidebar</p>
          </div>
        )}
      </div>

      {/* Extension Group Modals */}
      <ExtensionGroupModal
        mode={ExtensionGroupModalMode.CREATE}
        isOpen={modals.createOpen}
        onClose={() => setModals((prev) => ({ ...prev, createOpen: false }))}
        onCreateSuccess={async (data: ExtensionGroupFormData) => {
          await groupActions.createGroup(data.name, data.description, data.groupType);
          setModals((prev) => ({ ...prev, createOpen: false }));
        }}
      />

      <ExtensionGroupModal
        mode={ExtensionGroupModalMode.RENAME}
        isOpen={modals.renameOpen}
        onClose={() => setModals((prev) => ({ ...prev, renameOpen: false }))}
        groupName={selectedGroup?.name}
        groupDescription={selectedGroup?.description}
        onRenameSuccess={async (data: ExtensionGroupFormData) => {
          if (selectedGroup?.id) {
            await groupActions.renameGroup(selectedGroup.id, data.name, data.description);
          }
          setModals((prev) => ({ ...prev, renameOpen: false }));
        }}
      />

      <ExtensionGroupModal
        mode={ExtensionGroupModalMode.DELETE}
        isOpen={modals.deleteOpen}
        onClose={() => setModals((prev) => ({ ...prev, deleteOpen: false }))}
        groupName={selectedGroup?.name}
        onDeleteSuccess={async () => {
          if (selectedGroup?.id) {
            await groupActions.deleteGroup(selectedGroup.id);
          }
          setModals((prev) => ({ ...prev, deleteOpen: false }));
        }}
      />

      {/* Segment Rename Modal */}
      {showRenameSegmentModal !== null && selectedGroup && (
        <ConfirmationModal
          title={'Rename Segment'}
          buttonText={'Save'}
          mainContent={
            <div className="segment-rename-modal">
              <Input
                type="text"
                value={renameSegmentValue}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setRenameSegmentValue(e.target.value)}
                placeholder="Segment name"
              />
            </div>
          }
          isOpen={showRenameSegmentModal !== null}
          onClose={() => {
            setShowRenameSegmentModal(null);
            setRenameSegmentValue('');
          }}
          onSuccess={async () => {
            if (renameSegmentValue.trim()) {
              await segmentRuleActions.renameSegmentRule(selectedGroup.id, showRenameSegmentModal, renameSegmentValue);
            }
            setShowRenameSegmentModal(null);
            setRenameSegmentValue('');
          }}
        />
      )}

      {/* Segment Delete Modal */}
      {segmentToRemove && selectedGroup && (
        <ConfirmationModal
          title={'Delete Segment'}
          buttonText={'Delete'}
          mainContent={`Are you sure you want to delete "${segmentToRemove.name}"?`}
          isOpen={segmentToRemove !== null}
          onClose={() => setSegmentToRemove(null)}
          onSuccess={async () => {
            await segmentRuleActions.removeSegmentRule(selectedGroup.id, segmentToRemove.id);
            setSegmentToRemove(null);
          }}
          buttonVariant={'danger'}
        />
      )}
    </div>
  );
};
